import { useCallback } from 'react';

import { useLoginUser } from 'apps/auth/scripts/hooks';
import { useValidateOAuthUser } from 'apps/shared/scripts/graphql';

import LoginForm, { FormValues, LoginFormProps } from './LoginForm';

interface OwnProps {
  onFailure?: () => void;
  onSuccess?: () => void;
}

type FormProps = Pick<
  LoginFormProps,
  'onForgot' | 'onSignup' | 'modal' | 'onboarding'
>;

type Props = OwnProps & FormProps;

const LoginContainer = ({ onFailure, onSuccess, ...props }: Props) => {
  const { isLoading, login } = useLoginUser();
  const { mutate: validateOAuthUser } = useValidateOAuthUser();

  const handleLogin = useCallback(
    async ({ email, password }: FormValues) => {
      const { result, error } = await login(email, password);
      if (error.message || !result) {
        throw new Error(error.message);
      }
      if (onSuccess) {
        onSuccess();
      }
    },
    [login, onSuccess],
  );

  const handleOAuthLogin = useCallback(
    async (email: string) => {
      const { result, errors } = await validateOAuthUser({ input: { email } });
      if (!result || errors.length > 0) {
        if (onFailure) {
          onFailure();
        }
        throw new Error('No account with this email could be found.');
      }
      if (onSuccess) {
        onSuccess();
      }
    },
    [onFailure, onSuccess, validateOAuthUser],
  );

  return (
    <LoginForm
      {...props}
      handleOAuthLogin={handleOAuthLogin}
      handleLogin={handleLogin}
      isLoading={isLoading}
    />
  );
};

LoginContainer.displayName = 'LoginContainer';

export default LoginContainer;
